import type { MouseEvent as ReactMouseEvent } from 'react';
import { useIntl } from 'react-intl';

import { useBehavioralTrackingProps as _useBehavioralTrackingProps } from '../../../../core/hooks/useBehavioralTrackingProps.js';
import { ChartToolbarRenderer } from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import type { ChartToolbarRendererProps } from '../../../core/components/toolbar/types.js';
import { getMenuTriggerDisplayMode } from '../../../core/components/toolbar/utils/get-menu-trigger-display-mode.js';
import { getMenuTriggerIcon } from '../../../core/components/toolbar/utils/get-menu-trigger-icon.js';
import { ChartOverlaysElevation } from '../../../core/constants.js';
import { getA11yToolbarConfig } from '../../../core/utils/zoom.js';
import { defaultToolbarPlacement } from '../../constants/defaults.js';
import { useInteractionModeState } from '../../hooks/store/selectors.js';
import { useHistogramFilteredData } from '../../hooks/useHistogramFilteredData.js';
import { useHistogramInteractions } from '../../hooks/useHistogramInteractions.js';
import { useHistogramSelectionAreaRef } from '../../hooks/useHistogramSelectionAreaRef.js';
import { useHistogramToolbar } from '../../hooks/useHistogramToolbar.js';
import { HISTOGRAM_MESSAGES } from '../../messages/tooltip-text.js';
import { buildAndDownloadCsv } from '../../utils/build-and-download-csv.js';
import { DownloadDataButton } from './DownloadDataButton.js';
import { ExploreButton } from './ExploreButton.js';
import { PanButton } from './PanButton.js';
import { ResetButton } from './ResetButton.js';
import { ZoomInOutButtons } from './ZoomInOutButtons.js';
import { ZoomModeButton } from './ZoomModeButton.js';

type HistogramToolbarProps = {
  onMouseEnter?: (event?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
  onMouseLeave?: (event?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
  handlerRef?: ChartToolbarRendererProps['handlerRef'];
};

export const HistogramToolbar = ({
  onMouseEnter,
  onMouseLeave,
  handlerRef,
}: HistogramToolbarProps) => {
  const toolbarContext = useHistogramToolbar();
  const { placement, collapsed, downloadData, intents } = toolbarContext;
  const interactionMode = useInteractionModeState();
  /* oxlint-disable-next-line typescript/no-deprecated */
  const { zoom, zoomX, pan } = useHistogramInteractions();
  const intl = useIntl();
  const selectionAreaRef = useHistogramSelectionAreaRef();

  const isZoomEnabled = zoom?.enabled;
  const isPanEnabled = pan?.enabled;
  const isDownloadEnabled = downloadData?.enabled ?? false;

  const anyInteractionEnabled =
    isZoomEnabled ||
    zoomX?.enabled ||
    isPanEnabled ||
    isDownloadEnabled ||
    intents;

  const menuIcon = getMenuTriggerIcon(interactionMode, isZoomEnabled);

  const menuLabel =
    !isPanEnabled && !isZoomEnabled
      ? intl.formatMessage(HISTOGRAM_MESSAGES.zoomModeExplore)
      : undefined;

  const menuDisplayMode = getMenuTriggerDisplayMode({
    isPanEnabled,
    isZoomEnabled,
    isDownloadEnabled,
  });

  const startsExpanded = !collapsed;
  const hasExploreMenu =
    menuDisplayMode === 'always' ||
    (menuDisplayMode === 'expanded' && startsExpanded) ||
    (menuDisplayMode === 'collapsed' && !startsExpanded);

  const onClose = () =>
    selectionAreaRef ? selectionAreaRef.current?.focus() : undefined;

  const { series } = useHistogramFilteredData();

  /** Handles the download button when clicked. */
  const handleDownloadDataToolbar = () => buildAndDownloadCsv(series);

  const showDefaultToolbarDownloadButton = isDownloadEnabled && !intents;
  const showMenuToolbarButton = isDownloadEnabled && !!intents?.length;

  const behaviorTrackingProps = _useBehavioralTrackingProps(toolbarContext)[0];

  return anyInteractionEnabled ? (
    <ChartToolbarRenderer
      visibleWhenMouseInBounds
      handlerRef={handlerRef}
      placement={placement || defaultToolbarPlacement}
      collapsed={collapsed}
      draggable={true}
      initialA11yState={getA11yToolbarConfig({
        zoomX,
        pan,
        zoom,
        download: { enabled: showDefaultToolbarDownloadButton },
        intent: !!intents,
        hasExploreMenu,
      })}
      zIndex={ChartOverlaysElevation.toolbar}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      menuIcon={menuIcon}
      menuTriggerLabel={menuLabel}
      menuDisplayMode={menuDisplayMode}
      onClose={onClose}
      isDownloadEnabled={showMenuToolbarButton}
      intents={intents}
      handleDownloadDataToolbar={handleDownloadDataToolbar}
      {...behaviorTrackingProps}
    >
      <>
        <ExploreButton />
        {isZoomEnabled && <ZoomModeButton />}
        {isPanEnabled && <PanButton />}
      </>
      {isZoomEnabled && <ZoomInOutButtons />}
      <ResetButton />
      {showDefaultToolbarDownloadButton && <DownloadDataButton />}
    </ChartToolbarRenderer>
  ) : null;
};
